import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Comment } from '@frontend/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { PaginatedComments } from '../comments-paginated.model';
import {
  ConversationCommentResource,
  ConversationCommentListQuery,
  ConversationDefinition,
  ConversationPreview,
  Conversation,
} from '../models';
import { normaliseConversationError } from '../utils';

@Injectable({ providedIn: 'root' })
export class ConversationApiService {
  constructor(private http: HttpClient) {}

  getConversation(conversationId: number, guestUuid?: string): Observable<Conversation> {
    let params = new HttpParams();

    if (guestUuid) {
      params = params.set('guest_uuid', guestUuid);
    }

    return this.http
      .get<{ data: Conversation }>(`api/v1/conversations/${conversationId}`, {
        params,
      })
      .pipe(
        map((response) => response?.data as Conversation),
        catchError((error: HttpErrorResponse) =>
          throwError(() => normaliseConversationError(error, 'read'))
        )
      );
  }

  previewConversation(
    conversationDefinition: ConversationDefinition,
    guestUuid?: string
  ): Observable<ConversationPreview | Conversation> {
    const requestBody: {
      conversation_definition: ConversationDefinition;
      guest_uuid?: string;
    } = {
      conversation_definition: conversationDefinition,
    };

    if (guestUuid) {
      requestBody.guest_uuid = guestUuid;
    }

    return this.http
      .post<{ data: ConversationPreview | Conversation }>(
        'api/v1/conversations/preview',
        requestBody
      )
      .pipe(
        map((response) => response?.data as ConversationPreview | Conversation),
        catchError((error: HttpErrorResponse) => {
          if (error?.status === 404) {
            const conversation = this.getMissingResource(error);
            if (conversation) {
              return of(conversation);
            }
          }

          return throwError(() => normaliseConversationError(error, 'read'));
        })
      );
  }

  listCommentsByConversationId(
    conversationId: number,
    query: ConversationCommentListQuery = {},
    guestUuid?: string
  ): Observable<PaginatedComments> {
    let params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('per_page', String(query.per_page ?? 10))
      .set('sort_by', query.sort_by ?? 'created_at')
      .set('sort_direction', query.sort_direction ?? 'desc');

    if (guestUuid) {
      params = params.set('guest_uuid', guestUuid);
    }

    return this.http
      .get<PaginatedComments>(`api/v1/conversations/${conversationId}/comments`, {
        params,
      })
      .pipe(
        map((response) => this.normalisePaginatedComments(response, query.page ?? 1, query.per_page ?? 10)),
        catchError((error: HttpErrorResponse) =>
          throwError(() => normaliseConversationError(error, 'read'))
        )
      );
  }

  createCommentInConversation(
    conversationId: number,
    message: string,
    guestUuid?: string
  ): Observable<Comment> {
    const requestBody: { message: string; guest_uuid?: string } = { message };
    if (guestUuid) {
      requestBody.guest_uuid = guestUuid;
    }

    return this.http
      .post<{ data?: unknown }>(
        `api/v1/conversations/${conversationId}/comments`,
        requestBody
      )
      .pipe(
        map((response) => this.normaliseComment(response?.data ?? response)),
        catchError((error: HttpErrorResponse) =>
          throwError(() => normaliseConversationError(error, 'write'))
        )
      );
  }

  createCommentByDefinition(
    conversationDefinition: ConversationDefinition,
    message: string,
    guestUuid?: string
  ): Observable<ConversationCommentResource> {
    const requestBody: {
      conversation_definition: ConversationDefinition;
      message: string;
      guest_uuid?: string;
    } = {
      conversation_definition: conversationDefinition,
      message,
    };

    if (guestUuid) {
      requestBody.guest_uuid = guestUuid;
    }

    return this.http
      .post<{ data?: unknown }>('api/v1/conversations/comments', requestBody)
      .pipe(
        map((response) =>
          this.normaliseConversationComment(response?.data ?? response)
        ),
      catchError((error: HttpErrorResponse) =>
        throwError(() => normaliseConversationError(error, 'write'))
      )
      );
  }

  createReply(
    conversationId: number,
    commentId: number,
    message: string,
    guestUuid?: string
  ): Observable<Comment> {
    const requestBody: { message: string; guest_uuid?: string } = { message };

    if (guestUuid) {
      requestBody.guest_uuid = guestUuid;
    }

    return this.http
      .post<{ data?: unknown }>(
        `api/v1/conversations/${conversationId}/comments/${commentId}/replies`,
        requestBody
      )
      .pipe(
        map((response) => this.normaliseComment(response?.data ?? response)),
        catchError((error: HttpErrorResponse) =>
          throwError(() => normaliseConversationError(error, 'write'))
        )
      );
  }

  private getMissingResource(
    error: HttpErrorResponse
  ): (ConversationPreview | Conversation) | undefined {
    const backendError = (error?.error ?? {}) as {
      exists?: boolean;
      can_create?: boolean;
      data?: ConversationPreview | Conversation;
      message?: string;
      code?: string;
      meta?: unknown;
    };

    return backendError?.data;
  }

  private normalisePaginatedComments(
    response: PaginatedComments,
    currentPage: number,
    perPage: number
  ): PaginatedComments {
    const comments = (response?.data ?? []).map((comment) => this.normaliseComment(comment));

    return {
      data: comments,
      links: response?.links ?? {
        first: '',
        last: '',
        prev: '',
        next: '',
      },
      meta: response?.meta ?? {
        current_page: currentPage,
        from: 0,
        last_page: 1,
        path: '',
        per_page: perPage,
        to: 0,
        total: 0,
      },
    };
  }

  private normaliseComment(commentLike: any): Comment {
    return {
      ...commentLike,
      comment: commentLike?.comment ?? commentLike?.message ?? '',
      created_at: commentLike?.created_at
        ? new Date(commentLike.created_at)
        : commentLike?.created_at,
    } as Comment;
  }

  private normaliseConversationComment(
    commentLike: any
  ): ConversationCommentResource {
    return {
      ...this.normaliseComment(commentLike),
      conversation_id: Number(commentLike?.conversation_id),
      upvotes: Number(commentLike?.upvotes ?? 0),
      downvotes: Number(commentLike?.downvotes ?? 0),
      highlighted: Boolean(commentLike?.highlighted),
      replies: Array.isArray(commentLike?.replies)
        ? commentLike.replies.map((reply: any) =>
            this.normaliseConversationComment(reply)
          )
        : [],
    };
  }
}
